import React, { useEffect, useRef, useState } from "react";
import WaveSurfer from "wavesurfer.js";

import TimelinePlugin from 'wavesurfer.js/dist/plugins/timeline.esm.js';
import { useSelector, useDispatch } from 'react-redux';
import '@resources/assets/styles/editor.scss';
import { setSelectedPlayTime } from '@modules/media';
import { useParams } from "react-router-dom";

interface WaveSurferThumbnailViewerProps {
    videoDurationTime: number;
    zoomLevel?: number;
}

interface SpriteData {
    thumbnailCount: number;
    spriteCount: number;
    thumbnailWidth: number;
    thumbnailHeight: number;
}

interface SpriteMatrixItem {
    imgIdx: number;
    xPercent: number;
    yPercent: number;
}

const WaveSurferThumbnailViewer: React.FC<WaveSurferThumbnailViewerProps> = ({
    videoDurationTime,
    zoomLevel = 15,
}) => {
    const dispatch = useDispatch();
    const waveformRef = useRef<HTMLDivElement>(null);
    const wavesurfer = useRef<WaveSurfer | null>(null);
    const [spriteData, setSpriteData] = useState<SpriteData | null>(null);
    const [spriteMatrix, setSpriteMatrix] = useState<SpriteMatrixItem[]>([]);
    const [wrapperWidth, setWrapperWidth] = useState(0);
    const { uuid } = useParams();
    const cdn = useSelector((state: any) => state.organization.cdn);
    const playTime = useSelector((state: any) => state.media.playTime);

    const formWaveSurferOptions = () => ({
        container: waveformRef.current!,
        waveColor: "rgb(180, 222, 245, 0.5)",
        progressColor: "#99ccff",
        cursorColor: "#4b89dc",
        width: waveformRef.current ? waveformRef.current.clientWidth : 800,
        height: 200,
        normalize: true,
        minPxPerSec: zoomLevel,
        responsive: true,
        dragToSeek: true,
        scrollParent: false,
        plugins: [TimelinePlugin.create()],
    });

    const setupWaveSurferEvents = () => {
        wavesurfer.current?.on("ready", () => {
            const scrollElement = (wavesurfer.current as any).renderer.scrollContainer;
            scrollElement.addEventListener("wheel", (event: any) => {
                event.preventDefault();
                event.stopPropagation();
                scrollElement.scrollLeft += event.deltaY;
            });
            setWrapperWidth((wavesurfer.current as any).renderer.wrapper.offsetWidth);
            wavesurfer.current?.on("click", (time: number) => {
                dispatch(setSelectedPlayTime(time * videoDurationTime));
            });
        });
    };

    // 썸네일 데이터 fetch
    useEffect(() => {
        const fetchSpriteData = async () => {
            try {
                const response = await fetch(`${cdn}/${uuid}/extData/spriteData.json`);
                if (response.status === 200) {
                    const data = await response.json();
                    setSpriteData(data);
                    setSpriteMatrix(generateSpriteMatrix(data));
                }
            } catch (error) {
                console.error("Error fetching sprite data:", error);
            }
        };
        if (cdn && uuid) fetchSpriteData();
    }, [cdn, uuid]);

    const generateSpriteMatrix = (data: SpriteData): SpriteMatrixItem[] => {
        const thumbnailsPerSprite = Math.ceil(data.thumbnailCount / data.spriteCount);
        const spriteSide = Math.ceil(Math.sqrt(thumbnailsPerSprite));
        const matrix: SpriteMatrixItem[] = [];
        for (let i = 0; i < data.thumbnailCount; i++) {
            const imgIdx = Math.floor(i / (spriteSide * spriteSide)) + 1;
            const posInSprite = i % (spriteSide * spriteSide);
            const x = (posInSprite % spriteSide) * data.thumbnailWidth;
            const y = Math.floor(posInSprite / spriteSide) * data.thumbnailHeight;
            const xPercent = -1 * (x / data.thumbnailWidth) * 100;
            const yPercent = -1 * (y / data.thumbnailHeight) * 100;
            matrix.push({ imgIdx, xPercent, yPercent });
        }
        return matrix;
    };

    const displaySprites = (data: SpriteData, wrapperWidth: number, matrix: SpriteMatrixItem[], interval: number) => {
        if (!data || !wrapperWidth || !wavesurfer.current) return;
        const waveformWrapper = (wavesurfer.current as any).renderer.getWrapper();
        const widthPer2Second = (2 * wrapperWidth) / (videoDurationTime || 1);
        const heightPer2Second = widthPer2Second * (data.thumbnailHeight / data.thumbnailWidth);
        const existingWrapper = waveformWrapper.querySelector('.wrapper-sprite');
        if (existingWrapper) existingWrapper.remove();
        const spriteWrapper = document.createElement('div');
        spriteWrapper.className = 'wrapper-sprite';
        spriteWrapper.style.display = 'flex';
        spriteWrapper.style.position = 'absolute';
        spriteWrapper.style.top = 'calc(50% - 40px)';
        waveformWrapper.appendChild(spriteWrapper);
        for (let second = 0; second < Math.floor(videoDurationTime); second += interval) {
            const spriteDiv = document.createElement('div');
            spriteDiv.className = 'wrapper-thumbnail';
            spriteDiv.style.width = `${widthPer2Second}px`;
            spriteDiv.style.height = `${heightPer2Second}px`;
            const matrixIndex = Math.min(Math.floor(second / 10), matrix.length - 1);
            const { imgIdx, xPercent, yPercent } = matrix[matrixIndex];
            spriteDiv.style.backgroundImage = `url(${cdn}/${uuid}/extData/sprite/sprite-${imgIdx}.jpg)`;
            spriteDiv.style.backgroundSize = '300% 300%';
            spriteDiv.style.backgroundPosition = `${xPercent}% ${yPercent}%`;
            spriteWrapper.appendChild(spriteDiv);
        }
    };

    // WaveSurfer 초기화 및 리사이즈 핸들러
    useEffect(() => {
        const fetchPeaks = async () => {
            let peaksData;
            try {
                const response = await fetch(`${cdn}/${uuid}/extData/${uuid}.peaks`);
                if (!response.ok) throw new Error("Invalid response");
                peaksData = await response.json();
            } catch (error) {
                const samplesPerSecond = 100;
                const totalSamples = Math.ceil(videoDurationTime * samplesPerSecond);
                peaksData = new Array(totalSamples).fill(0).map(() => (Math.random() * 0.6 - 0.3));
            }
            if (wavesurfer.current) {
                wavesurfer.current.destroy();
            }
            const waveSurferOptions = formWaveSurferOptions();
            (waveSurferOptions as any).peaks = peaksData;
            (waveSurferOptions as any).duration = videoDurationTime;
            wavesurfer.current = WaveSurfer.create(waveSurferOptions);
            setupWaveSurferEvents();
        };
        if (videoDurationTime > 0 && waveformRef.current) {
            fetchPeaks();
        }
        const handleResize = () => {
            if (!waveformRef.current || !wavesurfer.current) return;
            const newWidth = waveformRef.current.clientWidth;
            // 현재 peaks와 currentTime 저장
            const currentPeaks = (wavesurfer.current as any).options.peaks;
            const currentTime = wavesurfer.current.getCurrentTime?.();
            // destroy 후 재생성
            wavesurfer.current.destroy();
            const newOptions = formWaveSurferOptions();
            (newOptions as any).peaks = currentPeaks;
            (newOptions as any).duration = videoDurationTime;
            wavesurfer.current = WaveSurfer.create(newOptions);
            setupWaveSurferEvents();
            // 이전 위치로 시크바 이동
            if (currentTime) {
                wavesurfer.current.seekTo(currentTime / videoDurationTime);
            }
            setWrapperWidth(newWidth);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [videoDurationTime]);

    // 썸네일 display useEffect
    useEffect(() => {
        if (wavesurfer.current && spriteData && spriteMatrix.length > 0 && wrapperWidth > 0) {
            const interval = zoomLevel <= 0 ? 10 : zoomLevel <= 25 ? 5 : zoomLevel <= 50 ? 2 : zoomLevel < 75 ? 1.5 : 1;
            displaySprites(spriteData, wrapperWidth, spriteMatrix, interval);
        }
    }, [spriteData, spriteMatrix, wrapperWidth, zoomLevel]);

    // wrapperWidth 갱신
    useEffect(() => {
        if (waveformRef.current) {
            setWrapperWidth(waveformRef.current.clientWidth);
        }
    }, [waveformRef.current]);

    // zoomLevel 변경 시 WaveSurfer zoom 업데이트
    useEffect(() => {
        if (wavesurfer.current && waveformRef.current) {
            wavesurfer.current.zoom(zoomLevel);
        }
    }, [zoomLevel]);

    useEffect(() => {
        if (wavesurfer.current && playTime > 0) {
            const waveCurrentTime = wavesurfer.current.getCurrentTime();
            if (Math.abs(playTime - waveCurrentTime) > 0.1) {
                wavesurfer.current.seekTo(playTime / videoDurationTime);
            }
        }
    }, [playTime]);

    return (
        <div id="waveform" ref={waveformRef} />
    );
};

export default WaveSurferThumbnailViewer; 